<template>
  <v-container class="py-6" style="max-width: 720px;">

    <!-- Profile incomplete banner (T0/T1 with pending invites) -->
    <v-alert
      v-if="profileState !== 'T2' && incomingInvites.length > 0"
      type="info"
      variant="tonal"
      class="mb-6"
      :title="profileState === 'T0' ? 'Completează profilul pentru a răspunde invitațiilor' : 'Completează profilul complet pentru a răspunde invitațiilor'"
      :text="profileState === 'T0'
        ? 'Cineva ți-a trimis o invitație. Descoperă-ți profilul psihologic pentru a putea accepta.'
        : 'Ești aproape! Completează a doua parte a profilului pentru a putea accepta invitații.'"
    >
      <template #append>
        <v-btn
          :to="profileState === 'T0' ? '/profile/t1' : '/profile/t2'"
          color="info"
          variant="tonal"
          size="small"
        >
          Continuă
        </v-btn>
      </template>
    </v-alert>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="success" />
    </div>

    <template v-else>

      <!-- INCOMING INVITES (recipient, CLAIMED) -->
      <template v-if="incomingInvites.length > 0">
        <h2 class="text-subtitle-1 font-weight-bold mb-3">Invitații primite</h2>
        <v-card
          v-for="match in incomingInvites"
          :key="match.id"
          rounded="lg"
          elevation="1"
          class="pa-5 mb-4"
          :class="{ 'opacity-50': profileState !== 'T2' }"
        >
          <div class="d-flex align-center mb-3">
            <v-avatar color="success" size="40" class="mr-3">
              <v-img :src="avatarUrl(match.initiator.uuid)">
                <template #error>
                  <div class="d-flex align-center justify-center fill-height">
                    <span class="text-body-2 font-weight-bold">{{ initials(match.initiator) }}</span>
                  </div>
                </template>
              </v-img>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ match.initiator.firstName }} {{ match.initiator.lastName }}</div>
              <div class="text-caption text-medium-emphasis">te-a invitat să verificați compatibilitatea</div>
            </div>
          </div>

          <div v-if="profileState === 'T2'" class="d-flex gap-2 mt-4">
            <v-btn
              color="success"
              variant="tonal"
              prepend-icon="mdi-check"
              :loading="actingOnMatch === match.id + '-accept'"
              @click="acceptMatch(match)"
            >
              Acceptă
            </v-btn>
            <v-btn
              color="error"
              variant="text"
              prepend-icon="mdi-close"
              :loading="actingOnMatch === match.id + '-decline'"
              @click="declineMatch(match)"
            >
              Refuză
            </v-btn>
          </div>
          <div v-else class="text-caption text-medium-emphasis mt-3">
            Completează profilul pentru a răspunde acestei invitații.
          </div>
        </v-card>
        <v-divider class="my-6" />
      </template>

      <!-- ACTIVE MATCHES (ACCEPTED / SCORED / DONE) -->
      <template v-if="activeMatches.length > 0">
        <h2 class="text-subtitle-1 font-weight-bold mb-3">Compatibilități</h2>
        <v-card
          v-for="match in activeMatches"
          :key="match.id"
          rounded="lg"
          elevation="1"
          class="pa-5 mb-4"
          :style="{
            cursor: 'pointer',
            borderLeft: match.state === 'DONE' && match.initiatorId === userId && !match.seenByInitiator
              ? '4px solid rgb(var(--v-theme-success))'
              : '4px solid transparent',
          }"
          @click="$router.push(`/matches/${match.id}`)"
        >
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-badge
                :model-value="match.state === 'DONE' && match.initiatorId === userId && !match.seenByInitiator"
                color="success"
                dot
                class="mr-3"
              >
                <v-avatar color="primary" size="40">
                  <v-img :src="otherPersonAvatarUrl(match)">
                    <template #error>
                      <div class="d-flex align-center justify-center fill-height">
                        <span class="text-body-2 font-weight-bold">{{ otherPersonInitials(match) }}</span>
                      </div>
                    </template>
                  </v-img>
                </v-avatar>
              </v-badge>
              <div>
                <div :class="match.state === 'DONE' && match.initiatorId === userId && !match.seenByInitiator ? 'font-weight-bold' : 'font-weight-medium'">{{ otherPersonName(match) }}</div>
                <div class="text-caption text-medium-emphasis" :class="{ 'font-weight-bold': match.state === 'DONE' && match.initiatorId === userId && !match.seenByInitiator }">{{ stateLabel(match) }}</div>
              </div>
            </div>
            <div class="text-right">
              <div v-if="match.scores != null" class="text-h6 font-weight-bold text-success">
                {{ match.scores.score }}%
              </div>
              <v-chip v-else :color="stateColor(match)" size="x-small" variant="tonal">
                {{ stateLabel(match) }}
              </v-chip>
            </div>
          </div>
        </v-card>
        <v-divider class="my-6" />
      </template>

      <!-- SENT LINKS (initiator) -->
      <template v-if="sentLinks.length > 0">
        <h2 class="text-subtitle-1 font-weight-bold mb-3">Invitații trimise</h2>
        <v-card
          v-for="match in sentLinks"
          :key="match.id"
          rounded="lg"
          elevation="1"
          class="pa-4 mb-3"
          :ripple="['CREATED', 'CLAIMED'].includes(match.state)"
          :style="['CREATED', 'CLAIMED'].includes(match.state) ? 'cursor: pointer' : ''"
          @click="['CREATED', 'CLAIMED'].includes(match.state) ? viewInvitation(match) : null"
        >
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="font-weight-medium">{{ match.recipientFirstName }} {{ match.recipientLastName || '' }}</div>
              <div class="text-caption text-medium-emphasis">{{ formatDate(match.createdAt) }}</div>
            </div>
            <div class="d-flex align-center ga-2">
              <v-chip :color="sentStateColor(match)" size="small" variant="tonal">
                {{ sentStateLabel(match) }}
              </v-chip>
              <v-btn
                v-if="['CREATED', 'CLAIMED'].includes(match.state)"
                icon="mdi-delete-outline"
                size="small"
                variant="text"
                color="error"
                @click.stop="matchToDelete = match; deleteConfirmDialog = true"
              />
            </div>
          </div>
        </v-card>
        <v-divider class="my-6" />
      </template>

      <!-- Empty state -->
      <div
        v-if="incomingInvites.length === 0 && activeMatches.length === 0 && sentLinks.length === 0"
        class="text-center py-10 text-medium-emphasis"
      >
        <v-icon size="48" class="mb-4">mdi-handshake-outline</v-icon>
        <p>Nicio potrivire încă.</p>
        <p v-if="profileState === 'T2'" class="text-caption mt-1">
          Generează primul tău link de compatibilitate.
        </p>
      </div>

    </template>

    <!-- FAB: new match (T2 only) -->
    <v-btn
      v-if="profileState === 'T2'"
      position="fixed"
      location="bottom end"
      style="margin: 24px;"
      color="success"
      size="large"
      prepend-icon="mdi-plus"
      elevation="4"
      @click="newMatchDialog = true"
    >
      Potrivire nouă
    </v-btn>

    <!-- New match dialog -->
    <v-dialog v-model="newMatchDialog" max-width="460">
      <v-card rounded="lg" class="pa-4">
        <v-card-title class="text-h6 mb-2">Invitație nouă</v-card-title>
        <v-card-text>

          <template v-if="!generatedLink">
            <p class="text-body-2 text-medium-emphasis mb-4">
              Profilul tău este privat. Tu și persoana invitată veți vedea doar rezultatul compatibilității.
            </p>
             <v-alert v-if="pendingCount >= maxPendingInvites" type="warning" variant="tonal" density="compact" class="mb-3">
              Ai ajuns la limita de {{ pendingCount }} invitații în așteptare.
              Așteaptă să răspundă altcineva sau elimină invitațiile vechi.
             </v-alert>
            <v-text-field
              v-model="newMatch.firstName"
              label="Prenume *"
              variant="outlined"
              density="comfortable"
              class="mb-3"
              :error-messages="newMatchError ? [newMatchError] : []"
            />
            <v-text-field
              v-model="newMatch.lastName"
              label="Nume (opțional)"
              variant="outlined"
              density="comfortable"
            />
          </template>

          <template v-else>
            <p class="text-body-2 text-medium-emphasis mb-4">
              Trimite acest link lui <strong>{{ newMatch.firstName }}</strong> prin orice canal preferi.
            </p>
            <v-text-field
              :model-value="generatedLink"
              variant="outlined"
              density="comfortable"
              readonly
              append-inner-icon="mdi-content-copy"
              @click:append-inner="copyLink"
            />
            <v-alert v-if="copied" type="success" variant="tonal" density="compact" class="mt-2">
              Link copiat!
            </v-alert>
            <div class="text-center mt-4">
              <canvas ref="qrCanvas" style="border-radius: 8px;" />
            </div>
            <div class="d-flex gap-2 mt-3">
              <v-btn
                variant="tonal"
                color="success"
                prepend-icon="mdi-content-copy"
                @click="copyLink"
              >
                Copiază
              </v-btn>
              <v-btn
                variant="tonal"
                color="primary"
                prepend-icon="mdi-share-variant"
                :style="!canShare ? 'opacity: 0.4' : ''"
                @click="shareLink"
              >
                Distribuie
              </v-btn>
            </div>
          </template>

        </v-card-text>
        <v-card-actions class="pt-0">
          <v-spacer />
          <v-btn variant="text" @click="closeNewMatchDialog">Închide</v-btn>
          <v-btn
            v-if="!generatedLink"
            color="success"
            variant="tonal"
            :loading="creatingMatch"
            :disabled="pendingCount >= maxPendingInvites || !newMatch.firstName.trim()"
            @click="createMatch"
          >
            Generează link
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <BotanicalIcon name="sprig" :size="74" :opacity="0.25" :rotate="10"
      style="position: fixed; bottom: 24px; left: calc(var(--v-layout-left) + 24px); color: rgb(var(--v-theme-primary));" />

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteConfirmDialog" max-width="360">
      <v-card rounded="lg">
        <v-card-title class="text-body-1 font-weight-bold pt-5 px-5">Retrage invitația?</v-card-title>
        <v-card-text class="px-5 pb-2 text-medium-emphasis">
          Invitația pentru <strong>{{ matchToDelete?.recipientFirstName }} {{ matchToDelete?.recipientLastName || '' }}</strong> va fi ștearsă definitiv.
        </v-card-text>
        <v-card-actions class="px-5 pb-4">
          <v-spacer />
          <v-btn variant="text" @click="deleteConfirmDialog = false">Anulează</v-btn>
          <v-btn color="error" variant="tonal" :loading="deletingMatch" @click="deleteMatch">Șterge</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { appStore } from '@/stores/AppStore'
import apiService from '@/services/ApiService'
import QRCode from 'qrcode'
import BotanicalIcon from '@/components/BotanicalIcon.vue'

const SERVER_BASE = process.env.VUE_APP_API_ENDPOINT.replace(/\/api$/, '')

export default {
  name: 'MatchesView',
  components: { BotanicalIcon },

  data() {
    return {
      loading: false,
      matches: [],
      actingOnMatch: null,  // '<id>-accept' or '<id>-decline'

      newMatchDialog: false,
      newMatch: { firstName: '', lastName: '' },
      newMatchError: null,
      creatingMatch: false,
      generatedLink: null,
      copied: false,

      deleteConfirmDialog: false,
      matchToDelete: null,
      deletingMatch: false,
    }
  },

  computed: {
    store() {
      return appStore()
    },
    profileState() {
      return this.store.user?.profileState ?? 'T0'
    },
    userId() {
      return this.store.user?.user_id
    },

    canShare() {
      return !!navigator.share
    },

    // CLAIMED matches where I am recipient
    incomingInvites() {
      return this.matches.filter(m =>
        m.state === 'CLAIMED' && m.recipientId === this.userId
      )
    },

    // ACCEPTED / SCORED / DONE — either party
    activeMatches() {
      return this.matches.filter(m =>
        ['ACCEPTED', 'SCORED', 'DONE'].includes(m.state)
      )
    },

    // matches where I am initiator (excludes those already in activeMatches)
    sentLinks() {
      return this.matches.filter(m =>
        m.initiatorId === this.userId &&
        !['ACCEPTED', 'SCORED', 'DONE'].includes(m.state)
      )
    },

      // T-904: how many pending invites (CREATED + CLAIMED) the user has open — mirrors backend pendingCountByInitiator
    pendingCount() {
      return this.sentLinks.filter(m => ['CREATED', 'CLAIMED'].includes(m.state)).length
    },

      // T-904: the configured limit for pending invites (mirrors backend MAX_PENDING_INVITES)
    maxPendingInvites() {
        return parseInt(process.env.VUE_APP_MAX_PENDING_INVITES || "3", 10)
      },
  },

  watch: {
    async generatedLink(url) {
      if (!url) return
      await this.$nextTick()
      QRCode.toCanvas(this.$refs.qrCanvas, url, { width: 200, margin: 2 })
    },
  },

  async created() {
    await this.loadMatches()
  },

  methods: {
    async loadMatches() {
      this.loading = true
      try {
        this.matches = await apiService.getMatches()
      } catch (err) {
        console.error('MatchesView > loadMatches error:', err)
      } finally {
        this.loading = false
      }
    },

    async deleteMatch() {
      this.deletingMatch = true
      try {
        await apiService.deleteMatch(this.matchToDelete.id)
        this.matches = this.matches.filter(m => m.id !== this.matchToDelete.id)
        this.deleteConfirmDialog = false
        this.matchToDelete = null
      } catch (err) {
        console.error('MatchesView > deleteMatch error:', err)
      } finally {
        this.deletingMatch = false
      }
    },


    async acceptMatch(match) {
      this.actingOnMatch = `${match.id}-accept` //trigger this button to spin
      try {
        await apiService.acceptMatch(match.id)
        await this.loadMatches()
      } catch (err) {
        console.error('MatchesView > acceptMatch error:', err)
        this.$toast.error('A apărut o eroare. Te rugăm să încerci din nou.')
      } finally {
        this.actingOnMatch = null //stop spinning
      }
    },

    async declineMatch(match) {
      this.actingOnMatch = `${match.id}-decline`
      try {
        await apiService.declineMatch(match.id)
        await this.loadMatches()
      } catch (err) {
        console.error('MatchesView > declineMatch error:', err)
      } finally {
        this.actingOnMatch = null
      }
    },

    async createMatch() {
      this.newMatchError = null
      this.creatingMatch = true
      try {
        const data = await apiService.createMatch({
          recipientFirstName: this.newMatch.firstName.trim(),
          recipientLastName: this.newMatch.lastName.trim() || undefined,
        })
        this.generatedLink = `${SERVER_BASE}/i/${data.token}`
        await this.loadMatches()
      } catch (err) {
        this.newMatchError = err.message || 'Eroare la generarea linkului.'
        console.error('MatchesView > createMatch error:', err)
      } finally {
        this.creatingMatch = false
      }
    },

    viewInvitation(match) {
      this.newMatch.firstName = match.recipientFirstName
      this.newMatch.lastName  = match.recipientLastName || ''
      this.generatedLink      = `${SERVER_BASE}/i/${match.token}`
      this.newMatchDialog     = true
    },

    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.generatedLink)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      } catch {
        // fallback: select the text field
      }
    },

    async shareLink() {
      if (!this.canShare) {
        this.$toast.error('Distribuirea nu este suportată pe acest dispozitiv.')
        return
      }
      try {
        await navigator.share({
          title: `Invitație Resonaro de la ${this.store.user?.firstName}`,
          text: `${this.store.user?.firstName} te invită să verificați compatibilitatea psihologică.`,
          url: this.generatedLink,
        })
      } catch {
        // user cancelled — no action needed
      }
    },

    closeNewMatchDialog() {
      this.newMatchDialog = false
      this.newMatch = { firstName: '', lastName: '' }
      this.newMatchError = null
      this.generatedLink = null
      this.copied = false
    },

    otherPersonName(match) {
      if (match.initiatorId === this.userId) {
        return match.recipient
          ? `${match.recipient.firstName} ${match.recipient.lastName || ''}`.trim()
          : `${match.recipientFirstName} ${match.recipientLastName || ''}`.trim()
      }
      return `${match.initiator.firstName} ${match.initiator.lastName || ''}`.trim()
    },

    otherPersonInitials(match) {
      const name = this.otherPersonName(match)
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    },

    avatarUrl(uuid) {
      return uuid ? `${SERVER_BASE}/api/auth/avatar/${uuid}` : null
    },

    otherPersonAvatarUrl(match) {
      const uuid = match.initiatorId === this.userId ? match.recipient?.uuid : match.initiator?.uuid
      return this.avatarUrl(uuid)
    },

    initials(user) {
      return `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    },

    // State labels for active matches (both parties)
    stateLabel(match) {
      const labels = {
        ACCEPTED: 'Acceptat',
        SCORED:   'Scor calculat',
        DONE:     'Raport disponibil',
      }
      return labels[match.state] ?? match.state
    },

    stateColor(match) {
      const colors = {
        ACCEPTED: 'info',
        SCORED:   'warning',
        DONE:     'success',
      }
      return colors[match.state] ?? 'default'
    },

    // State labels for sent links (initiator view) — BR-009: REJECTED/EXPIRED shown as "În așteptare"
    sentStateLabel(match) {
      const labels = {
        CREATED:  'Creat',
        CLAIMED:  'Vizualizat',
        REJECTED: 'În așteptare',
        EXPIRED:  'În așteptare',
      }
      return labels[match.state] ?? match.state
    },

    sentStateColor(match) {
      const colors = {
        CREATED:  'default',
        CLAIMED:  'info',
        REJECTED: 'default',
        EXPIRED:  'default',
      }
      return colors[match.state] ?? 'default'
    },

    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' })
    },
  },
}
</script>
